import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link2, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { AssistedLinkCard } from "./AssistedLinkCard";
import { AssistedSubmissionCard } from "./AssistedSubmissionCard";
import { GenerateLinkModal } from "./GenerateLinkModal";

interface AssistedCollectionTabProps {
  patientId: string;
}

export function AssistedCollectionTab({ patientId }: AssistedCollectionTabProps) {
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [initialLinkType, setInitialLinkType] = useState<'health_data' | 'first_consultation' | 'satisfaction_survey' | undefined>(undefined);
  const queryClient = useQueryClient();

  // Buscar links ativos
  const { data: links, isLoading: linksLoading, refetch: refetchLinks } = useQuery({
    queryKey: ['assisted-links', patientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assisted_collection_links')
        .select('*')
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Buscar submissões pendentes
  const { data: submissions, isLoading: submissionsLoading, refetch: refetchSubmissions } = useQuery({
    queryKey: ['assisted-submissions', patientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assisted_submissions')
        .select('*')
        .eq('patient_id', patientId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const activeLinks = links?.filter(link => {
    const now = new Date();
    const expiresAt = new Date(link.expires_at);
    return link.is_active && now < expiresAt;
  });

  const pendingCount = submissions?.length || 0;

  const handleRefresh = () => {
    refetchLinks();
    refetchSubmissions();
    toast.success("Atualizado!");
  };

  return (
    <div className="space-y-6">
      {/* Header com ações */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Links</h2>
          <p className="text-muted-foreground">
            Gere links de interação com seus pacientes conforme opções abaixo:
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => {
            setInitialLinkType(undefined);
            setShowGenerateModal(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Gerar Link
          </Button>
        </div>
      </div>

      {/* Botões de atalho para tipos de link */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => {
            setInitialLinkType('health_data');
            setShowGenerateModal(true);
          }}
        >
          Coleta dados de saúde
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setInitialLinkType('first_consultation');
            setShowGenerateModal(true);
          }}
        >
          Primeiro atendimento
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setInitialLinkType('satisfaction_survey');
            setShowGenerateModal(true);
          }}
        >
          Pesquisa de satisfação
        </Button>
      </div>

      {/* Submissões Pendentes */}
      {pendingCount > 0 && (
        <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
          <CardHeader>
            <CardTitle className="text-orange-900 dark:text-orange-100 flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
              </span>
              {pendingCount} Submiss{pendingCount === 1 ? 'ão' : 'ões'} Pendente{pendingCount === 1 ? '' : 's'}
            </CardTitle>
            <CardDescription>
              Revise e processe os dados enviados pelo paciente
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {submissionsLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                Carregando...
              </div>
            ) : (
              submissions?.map((submission) => (
                <AssistedSubmissionCard
                  key={submission.id}
                  submission={{
                    ...submission,
                    submission_type: submission.submission_type as 'photo' | 'audio' | 'file' | 'text',
                    metadata: typeof submission.metadata === 'object' && submission.metadata !== null ? submission.metadata as Record<string, unknown> : undefined
                  }}
                  onProcessed={() => {
                    refetchSubmissions();
                    refetchLinks();
                  }}
                />
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Links Ativos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Links Ativos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {linksLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Carregando...
            </div>
          ) : activeLinks && activeLinks.length > 0 ? (
            <div className="space-y-4">
              {activeLinks.map((link) => (
                <AssistedLinkCard
                  key={link.id}
                  link={link}
                  onUpdate={refetchLinks}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Link2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                Nenhum link ativo no momento
              </p>
              <Button onClick={() => {
                setInitialLinkType(undefined);
                setShowGenerateModal(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeiro Link
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Geração */}
      <GenerateLinkModal
        open={showGenerateModal}
        onOpenChange={(open) => {
          setShowGenerateModal(open);
          if (!open) {
            setInitialLinkType(undefined);
          }
        }}
        patientId={patientId}
        initialLinkType={initialLinkType}
        onSuccess={() => {
          refetchLinks();
          setShowGenerateModal(false);
          setInitialLinkType(undefined);
        }}
      />
    </div>
  );
}
